import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PortalAuth } from '../components/portal/PortalAuth';
import { PortalDashboard } from '../components/portal/PortalDashboard';
import { getMe, authPost } from '../components/portal/portalApi';
import { inputStyle, labelStyle } from '../components/quote/quoteStyles';

const Shell = ({ title, children }) => (
  <div>
    <Helmet><title>{title} | Legal Document Specialist</title><meta name="robots" content="noindex" /></Helmet>
    <Header />
    <section style={{ backgroundColor: 'var(--bg-page)', padding: '140px 0 100px 0', minHeight: '85vh' }}>
      <div className="container">{children}</div>
    </section>
    <Footer />
  </div>
);

const PortalPage = () => {
  const [user, setUser] = useState(undefined);
  useEffect(() => { getMe().then((u) => setUser(u)); }, []);

  return (
    <Shell title="Client Portal">
      {user === undefined && <p data-testid="portal-loading" style={{ color: 'var(--text-secondary)' }}>Loading...</p>}
      {user === null && (
        <div style={{ display: 'grid', gap: '2.5rem' }}>
          <div style={{ maxWidth: '640px', textAlign: 'center', margin: '0 auto' }}>
            <p style={{ color: 'var(--brand-primary)', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Client Self-Help Portal</p>
            <h1 className="heading-1" data-testid="portal-title" style={{ margin: '0 0 0.75rem', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>Everything about your matter, in one place</h1>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', margin: 0 }}>Track quotes and orders, download guides, and get plain-English answers from the AI document assistant - all online.</p>
          </div>
          <PortalAuth onAuthed={setUser} />
        </div>
      )}
      {user && <PortalDashboard user={user} onSignOut={() => setUser(null)} />}
    </Shell>
  );
};

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState({ error: '', ok: '' });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const data = await authPost('reset-password', { token: params.get('token') || '', password });
      setMsg({ ok: data.message, error: '' });
      setTimeout(() => navigate('/portal'), 1800);
    } catch (err) {
      setMsg({ error: err.message, ok: '' });
    }
  };

  return (
    <Shell title="Reset Password">
      <form onSubmit={submit} data-testid="reset-password-form" style={{ maxWidth: '420px', margin: '0 auto', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', padding: '2rem', display: 'grid', gap: '1rem' }}>
        <h1 className="heading-3" style={{ margin: 0 }}>Choose a new password</h1>
        <div>
          <label htmlFor="reset-password" style={labelStyle}>New password</label>
          <input id="reset-password" data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
        </div>
        {msg.error && <p data-testid="reset-error" role="alert" style={{ color: '#c53030', margin: 0 }}>{msg.error}</p>}
        {msg.ok && <p data-testid="reset-success" style={{ color: '#2f855a', margin: 0 }}>{msg.ok}</p>}
        <button type="submit" className="btn-primary" data-testid="reset-submit">Update password</button>
      </form>
    </Shell>
  );
};

export const PaymentResultPage = ({ outcome }) => {
  const [params] = useSearchParams();
  const [state, setState] = useState(outcome === 'cancel' ? 'cancelled' : 'checking');

  useEffect(() => {
    if (outcome === 'cancel') return undefined;
    const sessionId = params.get('session_id');
    if (!sessionId) { setState('error'); return undefined; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (['expired', 'failed'].includes(data.payment_status)) { setState('error'); return; }
      } catch { /* retry */ }
      if (attempts < 6) timer = setTimeout(poll, 2000); else setState('pending');
    };
    poll();
    return () => clearTimeout(timer);
  }, [outcome, params]);

  const copy = {
    checking: ['Confirming your payment...', 'This usually takes a few seconds.'],
    paid: ['Payment complete', 'Your purchase has been added to your portal library.'],
    pending: ['Payment is processing', "We'll unlock your purchase as soon as Stripe confirms it. Check your portal in a minute."],
    error: ['Payment not completed', 'No charge was made. You can try again from your portal.'],
    cancelled: ['Checkout cancelled', 'No charge was made.']
  }[state];

  return (
    <Shell title="Payment">
      <div data-testid={`payment-${state}`} style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', padding: '3rem 2rem' }}>
        <h1 className="heading-2" style={{ marginBottom: '0.75rem' }}>{copy[0]}</h1>
        <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>{copy[1]}</p>
        <a href="/portal" className="btn-primary" data-testid="payment-back-to-portal" style={{ textDecoration: 'none' }}>Go to my portal</a>
      </div>
    </Shell>
  );
};

export default PortalPage;
